import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Customer360 from "@/pages/Customer360";
import Portfolio from "@/pages/Portfolio";
import NotificationsPage from "@/pages/Notifications";
import Onboarding from "@/pages/Onboarding";
import OnboardingStatus from "@/pages/OnboardingStatus";
import ReKyc from "@/pages/ReKyc";
import Compliance from "@/pages/Compliance";
import Leads from "@/pages/Leads";
import ServiceRequests from "@/pages/ServiceRequests";
import Imports from "@/pages/Imports";
import BGIOF from "@/pages/BGIOF";
import Reconciliation from "@/pages/Reconciliation";
import MIS from "@/pages/MIS";
import Admin from "@/pages/Admin";
import Manual from "@/pages/Manual";

const STAFF_ONLY = ["/dashboard", "/customers", "/compliance", "/leads", "/imports", "/reconciliation", "/mis", "/admin"];

function Protected({ children, path }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner label="Starting Barjeel…" />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "customer" && STAFF_ONLY.includes(path)) return <Navigate to="/portfolio" replace />;
  return <Layout>{children}</Layout>;
}

function Routed() {
  const { user } = useAuth();
  const home = user?.role === "customer" ? "/portfolio" : "/dashboard";
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Navigate to={home} replace />} />
      <Route path="/dashboard" element={<Protected path="/dashboard"><Dashboard /></Protected>} />
      <Route path="/customers" element={<Protected path="/customers"><Customers /></Protected>} />
      <Route path="/customers/:id" element={<Protected path="/customers/:id"><Customer360 /></Protected>} />
      <Route path="/portfolio" element={<Protected path="/portfolio"><Portfolio /></Protected>} />
      <Route path="/notifications" element={<Protected path="/notifications"><NotificationsPage /></Protected>} />
      <Route path="/onboarding" element={<Protected path="/onboarding"><OnboardingStatus /></Protected>} />
      <Route path="/corporate-onboarding" element={<Protected path="/corporate-onboarding"><Onboarding /></Protected>} />
      <Route path="/rekyc" element={<Protected path="/rekyc"><ReKyc /></Protected>} />
      <Route path="/compliance" element={<Protected path="/compliance"><Compliance /></Protected>} />
      <Route path="/leads" element={<Protected path="/leads"><Leads /></Protected>} />
      <Route path="/service-requests" element={<Protected path="/service-requests"><ServiceRequests /></Protected>} />
      <Route path="/imports" element={<Protected path="/imports"><Imports /></Protected>} />
      <Route path="/bgiof" element={<Protected path="/bgiof"><BGIOF /></Protected>} />
      <Route path="/reconciliation" element={<Protected path="/reconciliation"><Reconciliation /></Protected>} />
      <Route path="/mis" element={<Protected path="/mis"><MIS /></Protected>} />
      <Route path="/admin" element={<Protected path="/admin"><Admin /></Protected>} />
      <Route path="/manual" element={<Protected path="/manual"><Manual /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6 text-center" data-testid="app-error-boundary">
          <p className="font-display text-2xl font-bold text-slate-900 dark:text-slate-50">Something went wrong</p>
          <p className="text-sm text-muted-foreground max-w-md">The page could not be displayed. Please reload to continue.</p>
          <Button data-testid="app-error-reload" onClick={() => { this.setState({ error: null }); window.location.href = "/"; }}>Reload Barjeel</Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <Routed />
        </ErrorBoundary>
        <Toaster position="bottom-right" richColors duration={3000} />
      </BrowserRouter>
    </AuthProvider>
  );
}
